import React, { useEffect, useMemo, useState } from "react"
import { Box, Grid, Paper } from "@mui/material"
import dayjs from "dayjs"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import SelectComponent from "src/v2/components/select/select-component"
import GenericRadioButton from "src/v2/components/radio-button/generic-radio-button"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as FileIcon } from "src/v2/assets/icons/file-icon.svg"
import PriceService from "src/services/price-service"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import TabelaAtualizacaoSpreads from "./components/tabela-atualizacao-spreads/tabela-atualizacao-spreads"
import TabelaInadimplentesLGD from "./components/tabela-inadimplentes-lgd/tabela-inadimplentes-lgd"


const TITLE = "Atualização de Spreads"

const AtualizacaoSpreadsPage = () => {
    const priceService = useMemo(() => new PriceService(), [])

    const [loadingDates, setLoadingDates] = useState(false)
    const [loadingAdimplentes, setLoadingAdimplentes] = useState(false)
    const [loadingInadimplentes, setLoadingInadimplentes] = useState(false)
    const [exporting, setExporting] = useState(false)

    const [adimplentesFilterQuery, setAdimplentesFilterQuery] = useState("")
    const [inadimplentesFilterQuery, setInadimplentesFilterQuery] = useState("")

    const [date, setDate] = useState("")
    const [dateOptions, setDateOptions] = useState([])

    const [selectedTable, setSelectedTable] = useState(
        Number(0)
    )

    const [radioItems] = useState([
        {
            value: 0,
            label: "ADIMPLENTES",
            disabled: false,
        },
        {
            value: 1,
            label: "INADIMPLENTES",
            disabled: false,
        },
    ])

    useEffect(() => {
        const loadDates = async () => {
            setLoadingDates(true)
            try {
                const response = await priceService.getLastDatesOcurrence()
                const dates = (response.body?.data || [])
                    .map((item) => dayjs(item))
                    .filter((item) => item.isValid())
                    .sort((first, second) => second.valueOf() - first.valueOf())

                setDateOptions(
                    dates.map((item) => ({
                        value: item.format(DATE_FORMAT),
                        label: item.format(DATE_FORMAT_SHOW),
                    }))
                )
                setDate(dates[0]?.format(DATE_FORMAT) || "")
            } catch (error) {
                dispatchSnackbar(
                    "Não foi possível carregar as datas, tente novamente",
                    "error"
                )
            } finally {
                setLoadingDates(false)
            }
        }

        loadDates()
    }, [priceService])

    const changeDate = (event) => {
        setDate(event.target.value)
    }

    const changeTable = (value) => {
        setSelectedTable(
            Number(value)
        )
    }

    const exportFile = async () => {
        if (!date || exporting) return

        const filterQuery =
            selectedTable === 0
                ? adimplentesFilterQuery
                : inadimplentesFilterQuery

        setExporting(true)

        try {
            const response =
                await priceService.exportUpdateSpreadsExport(
                    date,
                    filterQuery
                )

            const blob = response.body
            const downloadUrl = window.URL.createObjectURL(blob)

            const link = document.createElement("a")

            link.href = downloadUrl
            link.download = `precos-spreads-indicativos-${date}.xlsx`

            document.body.appendChild(link)
            link.click()
            link.remove()

            window.URL.revokeObjectURL(downloadUrl)

        } catch (error) {
            dispatchSnackbar(
                "Tivemos um problema ao exportar os dados",
                "error"
            )
        } finally {
            setExporting(false)
        }
    }

    const renderTable = () => {
        if (!date) return null

        return (
            <>
                <Box
                    sx={{
                        display: selectedTable === 0 ? "flex" : "none",
                        flex: 1,
                        minHeight: 0,
                        flexDirection: "column",
                    }}
                >
                    <TabelaAtualizacaoSpreads
                        date={date}
                        loading={loadingAdimplentes}
                        setLoading={setLoadingAdimplentes}
                        onFilterQueryChange={setAdimplentesFilterQuery}
                    />
                </Box>

                <Box
                    sx={{
                        display: selectedTable === 1 ? "flex" : "none",
                        flex: 1,
                        minHeight: 0,
                        flexDirection: "column",
                    }}
                >
                    <TabelaInadimplentesLGD
                        date={date}
                        loading={loadingInadimplentes}
                        setLoading={setLoadingInadimplentes}
                        onFilterQueryChange={setInadimplentesFilterQuery}
                    />
                </Box>
            </>
        )
    }

    return (
        <SideBarLayout
            title={TITLE}
            header
            loading={loadingDates}
            disablePageScroll
            variant="v1"
            headerImage="/background/eventos_headers.jpg"
            headerPosition="1% 42%"
            headerTitle={TITLE}
        >
            <Box
                sx={{
                    mt: -2,
                    height: "calc(100dvh - 202px)",
                    maxHeight: "calc(100dvh - 202px)",
                    minHeight: 0,
                    pb: "8px",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    gap: 2,
                }}
            >
                <Grid container sx={{ flexShrink: 0 }}>
                    <Grid item xs={12} xl={10}>
                        <Paper sx={{ p: 3 }}>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                                <Grid item sm={10} md={10} xl={10}>
                                    <Grid container direction="row" spacing={3} alignItems="center">
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SelectComponent
                                                label="Data de Referência"
                                                value={date}
                                                onChange={changeDate}
                                                options={dateOptions}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={6}
                                        >
                                            <GenericRadioButton
                                                radioButtonItems={
                                                    radioItems
                                                }
                                                defaultRadioValue={
                                                    0
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    changeTable(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                title=""
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item>
                                    <ButtonWithIcon
                                        size="md"
                                        icon={<FileIcon />}
                                        onClick={exportFile}
                                        disabled={!date || loadingDates || exporting}
                                    >
                                        {exporting ? "Preparando arquivo..." : "Exportar EXCEL"}
                                    </ButtonWithIcon>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper
                    sx={{
                        flex: 1,
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    {renderTable()}
                </Paper>
            </Box>
        </SideBarLayout>
    )
}

export default AtualizacaoSpreadsPage